import { TaskProductUsage } from '../api/queries/workOrders';
import type { ProductState, UsageState } from '../constants/phytosConstants';
import type { Mixture, MixtureProductUsage, Product, ProductId, ProductUsage, ProductUsageId } from '../slices/phyto';
import { isCustomProduct } from './phytos';

type MixtureLineSnapshot = {
  productName: string;
  usageLabel?: string;
  doseUnit?: string;
  isCustom: boolean;
  maxDose?: number;
  usageState?: UsageState;
  productState?: ProductState | '';
  amm?: string;
  ratio?: number;
  totalUnit?: string;
  defaultDose?: number;
  recoltDelay?: number;
  zntAquatique?: number;
  productReentryDelays?: string[];
  productFunctions?: string[];
};

export type MixtureLine = {
  productId: ProductId;
  productUsageId: ProductUsageId;
  dose: number;
  function?: string;
  batchNumber?: string;
  position?: number;
  snapshot?: MixtureLineSnapshot;
};

export type MixtureLineView = {
  productId: ProductId;
  productUsageId: ProductUsageId;
  productName: string;
  usageLabel?: string;
  dose: number;
  doseUnit?: string;
  function?: string;
  isCustom: boolean;
  maxDose?: number;
  usageState?: UsageState;
};

export function fromMixtureProductUsage(u: MixtureProductUsage): MixtureLine {
  return {
    productId: u.product_id,
    productUsageId: u.product_usage_id,
    dose: u.dose,
    function: u.function || undefined,
    snapshot: {
      productName: u.product_name,
      usageLabel: u.product_usage_usage_id,
      doseUnit: u.dose_unit,
      isCustom: u.is_custom_product,
      maxDose: u.product_usage_dose,
      defaultDose: u.product_usage_dose,
      usageState: u.product_usage_state,
      productState: u.product_state,
      amm: u.product_amm,
      zntAquatique: u.product_usage_znt,
      recoltDelay: u.product_usage_recolt_delay,
      productReentryDelays: u.product_reentry_delays,
      productFunctions: u.product_functions,
    },
  };
}

export function fromTaskProductUsage(u: TaskProductUsage): MixtureLine {
  return {
    productId: u.product_id ?? '',
    productUsageId: u.product_usage_id,
    dose: u.dose,
    function: u.function || undefined,
    batchNumber: u.batch_number,
    position: u.position,
    snapshot: {
      productName: u.product_name,
      usageLabel: u.usage_id,
      doseUnit: u.dose_unit,
      isCustom: u.is_custom_product,
      maxDose: u.default_dose,
      usageState: u.product_usage_state,
      productState: u.product_state,
      amm: u.amm,
      ratio: u.ratio,
      totalUnit: u.total_unit,
      defaultDose: u.default_dose,
      recoltDelay: u.recolt_delay,
      zntAquatique: u.znt_aquatique,
      productReentryDelays: u.product_reentry_delays,
      productFunctions: u.product_functions,
    },
  };
}

export function lineFromSelection(
  product: Product,
  usage: ProductUsage,
  dose: number,
  fn?: string,
  batchNumber?: string,
): MixtureLine {
  const custom = isCustomProduct(product);
  return {
    productId: product.id,
    productUsageId: usage.id,
    dose,
    function: fn,
    batchNumber,
    snapshot: {
      productName: product.name,
      usageLabel: custom ? undefined : usage.usage_id,
      doseUnit: usage.dose_unit,
      isCustom: custom,
      maxDose: usage.dose,
      usageState: usage.state,
    },
  };
}

export function resolveLineView(line: MixtureLine, productsList: Product[]): MixtureLineView {
  const product = productsList.find((p) => p.id === line.productId);
  const usage = product?.usages.find((u) => u.id === line.productUsageId);
  const isCustom = product ? isCustomProduct(product) : (line.snapshot?.isCustom ?? false);
  return {
    productId: line.productId,
    productUsageId: line.productUsageId,
    dose: line.dose,
    function: line.function,
    productName: product?.name ?? line.snapshot?.productName ?? '',
    usageLabel: isCustom ? undefined : (usage?.usage_id ?? line.snapshot?.usageLabel),
    doseUnit: usage?.dose_unit ?? line.snapshot?.doseUnit,
    maxDose: usage?.dose ?? line.snapshot?.maxDose,
    usageState: usage?.state ?? line.snapshot?.usageState,
    isCustom,
  };
}

export function toCreatePayload(line: MixtureLine): {
  product_usage_id: ProductUsageId;
  dose: number;
  function?: string;
} {
  return { product_usage_id: line.productUsageId, dose: line.dose, function: line.function };
}

export function toUpdatePayload(line: MixtureLine): { dose: number; function?: string } {
  return { dose: line.dose, function: line.function };
}

export function toTaskProductUsage(line: MixtureLine, productsList: Product[]): TaskProductUsage {
  const product = productsList.find((p) => p.id === line.productId);
  const usage = product?.usages.find((u) => u.id === line.productUsageId);
  const s = line.snapshot;
  return {
    product_usage_id: line.productUsageId,
    default_dose: usage?.dose ?? s?.defaultDose ?? 0,
    dose: line.dose,
    dose_unit: usage?.dose_unit ?? s?.doseUnit ?? '',
    is_custom_product: product ? isCustomProduct(product) : (s?.isCustom ?? false),
    total_quantity: 0,
    is_customized: true,
    position: line.position,
    product_name: product?.name ?? s?.productName ?? '',
    product_id: line.productId,
    ratio: s?.ratio ?? 1,
    total_unit: s?.totalUnit ?? 'L',
    amm: product?.amm ?? s?.amm,
    product_state:
      (product ? (isCustomProduct(product) ? '' : (product?.state ?? s?.productState)) : s?.productState) || undefined,
    product_usage_state: usage?.state ?? s?.usageState ?? undefined,
    batch_number: line.batchNumber,
    function: line.function,
    product_reentry_delays: product?.reentry_delays ?? s?.productReentryDelays ?? undefined,
    recolt_delay: usage?.recolt_delay ?? s?.recoltDelay ?? undefined,
    usage_id: usage?.usage_id ?? s?.usageLabel,
    znt_aquatique: usage?.znt_aquatique ?? s?.zntAquatique ?? undefined,
  };
}

export function mixtureToTaskProductUsages(mixture: Mixture, productsList: Product[]): TaskProductUsage[] {
  return (mixture.product_usages || []).map((u) => toTaskProductUsage(fromMixtureProductUsage(u), productsList));
}

export function diffMixtureLines(
  original: MixtureLine[],
  current: MixtureLine[],
): { toCreate: MixtureLine[]; toUpdate: MixtureLine[]; toDelete: ProductUsageId[] } {
  const origById = new Map(original.map((l) => [l.productUsageId, l]));
  const currById = new Map(current.map((l) => [l.productUsageId, l]));
  const toCreate = current.filter((l) => !origById.has(l.productUsageId));
  const toDelete = original.filter((l) => !currById.has(l.productUsageId)).map((l) => l.productUsageId);
  const toUpdate = current.filter((l) => {
    const o = origById.get(l.productUsageId);
    return !!o && (o.dose !== l.dose || (o.function || undefined) !== (l.function || undefined));
  });
  return { toCreate, toUpdate, toDelete };
}
